import React, { Fragment, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMap } from 'react-map-gl'
import { Theme, useTheme } from '@mui/material/styles'

import { Appliance, ApplianceType, Coordinates, GeoAppliance } from 'common/api/v1/types'
import { CircleData, CircleMarkers, Polyline } from '../Markers'
import { AppDispatch, GlobalState } from '../../../../store'
import { getGeoAppliances } from '../../../../redux/actions/applianceActions'
import type { PopupProps } from '../utils'
import { getBounds } from '../../../../utils'
import { notUndefinedOrNull } from 'common/api/v1/helpers'
import { pick } from 'common/utils'
import { equals } from 'common/util'

const getLocationString = (point: Coordinates) => `${point[0]}:${point[1]}`

export type LocatedAppliance = Pick<Required<GeoAppliance>, 'id' | 'name' | 'type' | 'region' | 'geoLocation'> & {
  owner?: Appliance['owner']
}
/**
 * Returns points to show on map and connections between core nodes from different regions
 * @param appliances - array of GeoAppliances
 * @return {
 *   coreNodes: {'ltd:lng': {coordinates, name, id of coreNode}}
 *   coreConnections: {'ltd:lng-ltd:lng': [outputCoreNodeCoordinates, inputCoreNodeCoordinates]}
 *   dots: {'ltd:lng': array of appliances with these coordinates}
 * }
 */
const getMapObjects = (
  appliances: Array<Omit<LocatedAppliance, 'geoLocation'> & { geoLocation?: LocatedAppliance['geoLocation'] }>,
): {
  coreNodes: { [regionId: string]: { coordinates: Coordinates; name: string; id: string } }
  coreConnections: Array<{
    srcApplianceId: string
    srcApplianceCoords: Coordinates
    dstApplianceId: string
    dstApplianceCoords: Coordinates
  }>
  dots: { [applianceLocation: string]: LocatedAppliance[] }
} => {
  const coreNodes: { [regionId: string]: { coordinates: Coordinates; name: string; id: string } } = {}
  const dots = appliances.reduce<{ [location: string]: Array<LocatedAppliance> }>((acc, appliance) => {
    if (!appliance.geoLocation?.coordinates) return acc
    if (appliance.type === ApplianceType.core && appliance.region) {
      coreNodes[appliance.region.id] = {
        id: appliance.id,
        name: appliance.name,
        coordinates: appliance.geoLocation.coordinates,
      }
    }
    const applianceLocation = getLocationString(appliance.geoLocation.coordinates)
    const appliancesAtLocation = [...(acc[applianceLocation] || []), appliance as LocatedAppliance]
    return { ...acc, [applianceLocation]: appliancesAtLocation }
  }, {})
  const coreNodeValues = Object.values(coreNodes)

  const coreConnections = coreNodeValues.reduce<
    Array<{
      srcApplianceId: string
      srcApplianceCoords: Coordinates
      dstApplianceId: string
      dstApplianceCoords: Coordinates
    }>
  >((acc, { id, coordinates }, ind) => {
    for (let i = ind + 1; i < coreNodeValues.length; i++) {
      // Connect the core nodes in each region with all core nodes in each other region
      const dstAppliance = coreNodeValues[i]
      acc.push({
        srcApplianceId: id,
        srcApplianceCoords: coordinates,
        dstApplianceId: dstAppliance.id,
        dstApplianceCoords: dstAppliance.coordinates,
      })
    }
    return acc
  }, [])
  return { coreNodes, coreConnections, dots }
}
/**
 * returns the color for both line and marker
 * @param theme
 * @param isCoreNode - so it is either green for core node, or blue for other appliances
 */
export const getColor = (theme: Theme, isCoreNode = false) => {
  const colors = {
    appliance: theme.palette.primary.main,
    coreNode: theme.palette.success.light,
  }
  return !isCoreNode ? colors.appliance : colors.coreNode
}

interface ServiceProps {
  layerId: string
  setPopup: (p: undefined | PopupProps) => void
  featureEvents: { appliances: EventTarget }
}

/**
 * Overview page map
 * Two iterations over dots map, since we have to first draw lines and then markers in order for markers to be on top
 */
const Appliances: React.FunctionComponent<ServiceProps> = ({ layerId, setPopup, featureEvents }) => {
  const theme: Theme = useTheme()
  const { current: map } = useMap()
  const dispatch = useDispatch<AppDispatch>()
  useEffect(() => {
    dispatch(getGeoAppliances({ rowsPerPage: '100', pageNumber: '0' }))
  }, [dispatch])

  const { loading, geoAppliances } = useSelector(
    ({ appliancesReducer }: GlobalState) => ({
      loading: appliancesReducer.loading,
      geoAppliances: appliancesReducer.geoAppliances.map((ga) => ({
        ...pick(ga, 'id', 'name', 'type', 'region', 'geoLocation'),
        owner: 'owner' in ga ? ga.owner : undefined,
      })),
    }),
    equals,
  )
  const { dots, coreNodes, coreConnections } = getMapObjects(geoAppliances)
  useEffect(() => {
    const points = Object.values(dots).reduce<Array<Coordinates>>(
      (acc, appliances) => [...acc, ...appliances.map((a) => a.geoLocation?.coordinates)],
      [],
    )
    if (map && points.length) {
      // Animations enabled makes map.getCenter() and map.getZoom() return the pre-fitBounds() values which causes setViewport()
      // not work and the map to jump around when the user starts dragging
      map.fitBounds(getBounds(points), { padding: 40, animate: false })
    }
  }, [map, geoAppliances])

  if (loading) return null

  const circles: CircleData[] = Object.entries(dots)
    .map(([_, appliances], index) => {
      const { geoLocation } = appliances[0]
      if (!geoLocation) return null
      return { id: index, coordinates: geoLocation.coordinates, appliances }
    })
    .filter(notUndefinedOrNull)

  return (
    <>
      {coreConnections.map(({ srcApplianceId, srcApplianceCoords, dstApplianceId, dstApplianceCoords }) => (
        <Polyline
          key={`${srcApplianceId}-${dstApplianceId}`}
          color={getColor(theme, true)}
          width={2}
          position={[srcApplianceCoords, dstApplianceCoords]}
        />
      ))}
      {Object.entries(dots).map(([k, appliances]) => {
        const { geoLocation } = appliances[0]
        if (!geoLocation) return null
        const point = geoLocation.coordinates
        return (
          <Fragment key={k}>
            {appliances.map(({ region, type, id }) => {
              if (type === ApplianceType.core) return null
              const { coordinates: coreNodeLocation } =
                region?.id && coreNodes[region.id] ? coreNodes[region.id] : { coordinates: null }
              if (!coreNodeLocation) return null
              return (
                <Polyline
                  key={`appliance-connection-${id}`}
                  color={getColor(theme)}
                  width={2}
                  position={[point, coreNodeLocation]}
                />
              )
            })}
          </Fragment>
        )
      })}
      <CircleMarkers layerId={layerId} circles={circles} featureEvents={featureEvents} setPopup={setPopup} />
    </>
  )
}

export default Appliances
